import { useRef } from "react";
import { Link } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import useEmblaCarousel from "embla-carousel-react";
import { useQuery } from "@tanstack/react-query";
import {
  UtensilsCrossed, MapPin, CalendarDays, PartyPopper, Star,
  ChevronLeft, ChevronRight, Briefcase, GraduationCap, Users,
  Trophy, Heart, Sparkles, HandHeart,
} from "lucide-react";
import { LineReveal, Reveal } from "@/components/Reveal";
import { Marquee } from "@/components/Marquee";
import { Burst } from "@/components/Burst";
import { fetchReviews } from "@/lib/api";

const FEATURED = [
  {
    id: "giant-cinnamon-rolls",
    title: "Giant Cinnamon Rolls",
    desc: "Warm, gooey, and covered in sweet icing.",
    price: "$4",
    note: "4 for $15",
    img: "/images/cinnamon-roll.png",
    badge: "Bestseller",
    badgeColor: "bg-cb-pink text-white",
    tilt: "-rotate-2",
  },
  {
    id: "biscuits-and-gravy",
    title: "Biscuits & Gravy",
    desc: "Homestyle comfort food made fresh.",
    price: "$3",
    note: "2 biscuits $6",
    img: "/images/biscuits-gravy.png",
    badge: "Homestyle",
    badgeColor: "bg-cb-teal text-cb-black",
    tilt: "rotate-2",
  },
  {
    id: "fresh-coffee",
    title: "Fresh Coffee",
    desc: "The perfect way to start your day.",
    price: "$3",
    note: "16 oz, hot & fresh",
    img: "/images/coffee.png",
    badge: "Wake Up",
    badgeColor: "bg-cb-yellow text-cb-black",
    tilt: "-rotate-1",
  },
];

const EVENT_TYPES = [
  { name: "Office Parties", icon: Briefcase },
  { name: "Employee Appreciation", icon: HandHeart },
  { name: "School Events", icon: GraduationCap },
  { name: "Church Events", icon: Users },
  { name: "Community Events", icon: Sparkles },
  { name: "Fundraisers", icon: Trophy },
  { name: "Special Celebrations", icon: Heart },
];

const HERO_BUTTONS = [
  { text: "View Menu", path: "/menu", cls: "cb-btn-pink", icon: UtensilsCrossed, testid: "hero-view-menu-btn" },
  { text: "Find The Truck", path: "/truck", cls: "cb-btn-yellow", icon: MapPin, testid: "hero-find-truck-btn" },
  { text: "Upcoming Events", path: "/truck#events", cls: "cb-btn-teal", icon: CalendarDays, testid: "hero-events-btn" },
  { text: "Book Crazy Buns", path: "/book", cls: "cb-btn-white", icon: PartyPopper, testid: "hero-book-crazy-buns-btn" },
];

const Reviews = () => {
  const { data: reviews = [] } = useQuery({ queryKey: ["reviews"], queryFn: fetchReviews });
  const [emblaRef, emblaApi] = useEmblaCarousel({ loop: true, align: "start" });

  return (
    <section data-testid="reviews-section" className="bg-cb-cream py-20 sm:py-28 relative overflow-hidden">
      <div className="max-w-7xl mx-auto px-4">
        <Reveal className="text-center mb-12">
          <span className="cb-badge bg-cb-teal text-cb-black mb-4">5-Star Smiles</span>
          <h2 className="font-display font-bold uppercase text-2xl sm:text-3xl lg:text-4xl tracking-tight">
            Folks Are <span className="text-cb-pink">Talking</span>
          </h2>
        </Reveal>

        <Reveal delay={0.1}>
          <div className="overflow-hidden" ref={emblaRef} data-testid="reviews-carousel">
            <div className="flex gap-6">
              {(reviews.length ? reviews : []).map((r, i) => (
                <div key={i} className="min-w-0 flex-[0_0_100%] sm:flex-[0_0_50%] lg:flex-[0_0_33.333%]">
                  <div className={`cb-card p-7 h-full flex flex-col ${i % 2 ? "rotate-1" : "-rotate-1"}`}>
                    <div className="flex gap-1 mb-4" aria-label={`${r.rating} star review`}>
                      {Array.from({ length: r.rating }).map((_, s) => (
                        <Star key={s} className="w-5 h-5 text-cb-yellow fill-cb-yellow stroke-cb-black" strokeWidth={1.5} />
                      ))}
                    </div>
                    <p className="font-bold text-base leading-relaxed flex-1">&ldquo;{r.comment}&rdquo;</p>
                    <div className="mt-6 flex items-center gap-3">
                      <div className={`w-11 h-11 rounded-full border-3 border-cb-black flex items-center justify-center font-display font-bold ${["bg-cb-pink text-white", "bg-cb-teal", "bg-cb-yellow", "bg-cb-black text-cb-yellow"][i % 4]}`}>
                        {r.name?.charAt(0)}
                      </div>
                      <div>
                        <div className="font-display font-semibold text-sm uppercase">{r.name}</div>
                        <div className="text-xs font-bold text-cb-black/60">{r.role}</div>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
          <div className="flex justify-center gap-3 mt-8">
            <button data-testid="reviews-prev-btn" onClick={() => emblaApi?.scrollPrev()} aria-label="Previous review"
              className="w-12 h-12 rounded-full border-3 border-cb-black bg-cb-yellow shadow-hard-sm flex items-center justify-center hover:bg-cb-teal transition-colors active:translate-x-0.5 active:translate-y-0.5 active:shadow-none">
              <ChevronLeft className="w-5 h-5" />
            </button>
            <button data-testid="reviews-next-btn" onClick={() => emblaApi?.scrollNext()} aria-label="Next review"
              className="w-12 h-12 rounded-full border-3 border-cb-black bg-cb-yellow shadow-hard-sm flex items-center justify-center hover:bg-cb-teal transition-colors active:translate-x-0.5 active:translate-y-0.5 active:shadow-none">
              <ChevronRight className="w-5 h-5" />
            </button>
          </div>
        </Reveal>
      </div>
    </section>
  );
};

export default function Home() {
  const heroRef = useRef(null);
  const { scrollYProgress } = useScroll({ target: heroRef, offset: ["start start", "end start"] });
  const bgY = useTransform(scrollYProgress, [0, 1], ["0%", "22%"]);
  const bgScale = useTransform(scrollYProgress, [0, 1], [1.05, 1.18]);
  const stickerY = useTransform(scrollYProgress, [0, 1], ["0%", "-60%"]);
  const fade = useTransform(scrollYProgress, [0, 0.7], [1, 0]);

  return (
    <div data-testid="home-page">
      {/* ---------- HERO ---------- */}
      <section ref={heroRef} data-testid="hero-section" className="relative min-h-[94vh] flex items-center overflow-hidden bg-cb-black">
        <motion.div style={{ y: bgY, scale: bgScale }} className="absolute inset-0">
          <img
            src="/images/hero-truck.png"
            alt="Crazy Buns food trailer serving cinnamon rolls"
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-cb-black/85 via-cb-black/45 to-cb-black/10" />
          <div className="absolute inset-0 bg-gradient-to-t from-cb-black/70 via-transparent to-cb-black/30" />
        </motion.div>

        <motion.div style={{ opacity: fade }} className="relative z-10 max-w-7xl mx-auto px-4 py-24 w-full">
          <motion.span
            initial={{ opacity: 0, scale: 0.6, rotate: -10 }}
            animate={{ opacity: 1, scale: 1, rotate: -2 }}
            transition={{ type: "spring", stiffness: 200, damping: 14 }}
            className="cb-badge bg-cb-yellow text-cb-black mb-6"
            data-testid="hero-badge"
          >
            Fresh · Hot · Delicious
          </motion.span>

          <h1 className="font-display font-bold uppercase leading-[0.95] tracking-tight text-4xl sm:text-5xl lg:text-6xl" data-testid="hero-headline">
            <LineReveal
              lines={[
                <span key="a" className="text-outline-yellow inline-block -rotate-1">Big Buns,</span>,
                <span key="b" className="text-outline-pink inline-block rotate-1">Big Breakfast,</span>,
                <span key="c" className="text-outline-white inline-block -rotate-1">Big Smiles!</span>,
              ]}
            />
          </h1>

          <motion.p
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.75, type: "spring", stiffness: 100, damping: 16 }}
            className="mt-6 max-w-xl text-white font-bold text-base md:text-lg leading-relaxed"
            data-testid="hero-subheadline"
          >
            Crazy Buns serves up warm cinnamon rolls, biscuits & gravy, fresh coffee, and breakfast
            favorites that make every morning better.
          </motion.p>

          <div className="mt-9 flex flex-wrap gap-3.5">
            {HERO_BUTTONS.map((b, i) => (
              <motion.div
                key={b.path + b.text}
                initial={{ opacity: 0, y: 28 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.95 + i * 0.12, type: "spring", stiffness: 160, damping: 15 }}
              >
                <Link to={b.path} data-testid={b.testid} className={`cb-btn ${b.cls}`}>
                  <b.icon className="w-4.5 h-4.5 w-5 h-5" /> {b.text}
                </Link>
              </motion.div>
            ))}
          </div>
        </motion.div>

        <motion.div style={{ y: stickerY }} className="absolute right-6 top-24 z-10 hidden md:block">
          <motion.img
            src="/logo.jpg"
            alt="Crazy Buns mascot"
            initial={{ opacity: 0, scale: 0.5, rotate: 12 }}
            animate={{ opacity: 1, scale: 1, rotate: 6 }}
            transition={{ delay: 1.1, type: "spring", stiffness: 120, damping: 12 }}
            className="w-40 lg:w-52 rounded-full border-4 border-cb-black shadow-hard-xl animate-floaty"
          />
        </motion.div>
        <motion.div style={{ y: stickerY }} className="absolute right-10 bottom-28 z-10 hidden md:block">
          <motion.div
            initial={{ opacity: 0, scale: 0.4 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ delay: 1.3, type: "spring", stiffness: 150, damping: 11 }}
          >
            <Burst text="Fresh Daily" subtext="Baked at sunrise" color="#FF1493" textColor="#FFFFFF" className="w-32 lg:w-40 animate-wiggle" rotate="rotate-6" />
          </motion.div>
        </motion.div>
      </section>

      <Marquee />

      {/* ---------- FEATURED FOOD ---------- */}
      <section data-testid="featured-food-section" className="bg-cb-cream halftone-light py-20 sm:py-28">
        <div className="max-w-7xl mx-auto px-4">
          <Reveal className="text-center mb-14">
            <span className="cb-badge bg-cb-pink text-white mb-4">Fan Favorites</span>
            <h2 className="font-display font-bold uppercase text-2xl sm:text-3xl lg:text-4xl tracking-tight">
              The <span className="text-cb-pink">Big Three</span>
            </h2>
            <p className="mt-3 font-bold text-cb-black/60 text-base md:text-lg">The stuff dreams (and mornings) are made of.</p>
          </Reveal>

          <div className="grid gap-10 sm:gap-8 md:grid-cols-3">
            {FEATURED.map((f, i) => (
              <Reveal key={f.id} delay={i * 0.12}>
                <article
                  data-testid={`food-card-${f.id}`}
                  className={`cb-card cb-card-hover overflow-hidden ${f.tilt} hover:rotate-0 h-full flex flex-col`}
                >
                  <div className="relative border-b-3 border-cb-black overflow-hidden aspect-[4/3] bg-cb-teal">
                    <img src={f.img} alt={f.title} className="w-full h-full object-cover hover:scale-105 transition-transform duration-500" />
                    <span className={`cb-badge ${f.badgeColor} absolute top-4 left-4`}>{f.badge}</span>
                  </div>
                  <div className="p-6 flex flex-col flex-1">
                    <h3 className="font-display font-semibold uppercase text-xl sm:text-2xl">{f.title}</h3>
                    <p className="mt-2 font-bold text-cb-black/60 text-sm sm:text-base flex-1">{f.desc}</p>
                    <div className="mt-5 flex items-center justify-between">
                      <div className="flex items-end gap-2">
                        <span className="font-display font-bold text-3xl text-cb-pink">{f.price}</span>
                        <span className="font-bold text-xs text-cb-black/60 uppercase mb-1">{f.note}</span>
                      </div>
                      <Link to="/menu" data-testid={`food-card-${f.id}-cta`} className="cb-btn cb-btn-yellow !px-4 !py-2 !text-xs">
                        See Menu
                      </Link>
                    </div>
                  </div>
                </article>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* ---------- EVENTS ---------- */}
      <section data-testid="events-section" className="bg-cb-teal border-y-3 border-cb-black py-20 sm:py-28 relative overflow-hidden">
        <div className="hidden md:block absolute -top-10 -right-10 w-56 h-56 rounded-full bg-cb-yellow border-3 border-cb-black opacity-60 animate-spin-slow" style={{ borderRadius: "42% 58% 55% 45% / 45% 42% 58% 55%" }} />
        <div className="max-w-7xl mx-auto px-4 relative">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <Reveal>
              <span className="cb-badge bg-white text-cb-black mb-4">We Cater!</span>
              <h2 className="font-display font-bold uppercase text-2xl sm:text-3xl lg:text-4xl tracking-tight">
                Bring Crazy Buns <span className="text-cb-pink">To You!</span>
              </h2>
              <p className="mt-4 font-bold text-cb-black/70 text-base md:text-lg leading-relaxed">
                We bring big breakfasts and even bigger smiles to events of all sizes. The trailer,
                the oven, the coffee, the good vibes — we roll in, you take the credit.
              </p>
              <Link to="/book" data-testid="events-book-btn" className="cb-btn cb-btn-pink mt-7">
                <PartyPopper className="w-5 h-5" /> Book Crazy Buns
              </Link>
            </Reveal>

            <div className="grid grid-cols-2 gap-4">
              {EVENT_TYPES.map((e, i) => (
                <Reveal key={e.name} delay={i * 0.07}>
                  <div
                    data-testid={`event-type-${e.name.toLowerCase().replace(/\s+/g, "-")}`}
                    className={`cb-card cb-card-hover p-4 flex items-center gap-3 ${i % 3 === 0 ? "-rotate-1" : i % 3 === 1 ? "rotate-1" : "rotate-0"}`}
                  >
                    <div className={`w-11 h-11 shrink-0 rounded-2xl border-3 border-cb-black flex items-center justify-center ${["bg-cb-pink text-white", "bg-cb-yellow", "bg-white", "bg-cb-black text-cb-yellow"][i % 4]}`}>
                      <e.icon className="w-5 h-5" />
                    </div>
                    <span className="font-display font-semibold uppercase text-xs sm:text-sm leading-tight">{e.name}</span>
                  </div>
                </Reveal>
              ))}
            </div>
          </div>
        </div>
      </section>

      <Reviews />

      {/* ---------- FINAL CTA ---------- */}
      <section data-testid="home-final-cta" className="bg-cb-black py-20 sm:py-24 relative overflow-hidden">
        <div className="absolute inset-0 halftone opacity-20" />
        <div className="max-w-4xl mx-auto px-4 text-center relative">
          <Reveal>
            <img src="/logo.jpg" alt="Crazy Buns logo" className="w-24 h-24 rounded-full border-4 border-cb-yellow object-cover mx-auto mb-6 animate-floaty" />
            <h2 className="font-display font-bold uppercase text-2xl sm:text-3xl lg:text-4xl tracking-tight text-white">
              Hungry? <span className="text-cb-yellow">Come Get A Bun.</span>
            </h2>
            <p className="mt-4 text-white/70 font-bold text-base md:text-lg">
              Find the truck, grab a roll, and start your morning the crazy way.
            </p>
            <div className="mt-8 flex flex-wrap justify-center gap-4">
              <Link to="/truck" data-testid="final-find-truck-btn" className="cb-btn cb-btn-yellow">
                <MapPin className="w-5 h-5" /> Find The Truck
              </Link>
              <Link to="/book" data-testid="final-book-btn" className="cb-btn cb-btn-pink">
                <PartyPopper className="w-5 h-5" /> Book Us For Your Event
              </Link>
            </div>
          </Reveal>
        </div>
      </section>
    </div>
  );
}
